import { cn } from '@/lib/utils'

/**
 * The glass panel used by every "account" surface — login, registration and
 * checkout. Kept in one place so the three never drift apart: a visitor who
 * signs in and then buys should feel they never left the same card.
 */
export const AUTH_ACCENT_GRADIENT = 'var(--flou-gradient)'
export const AUTH_ACCENT_GLOW = '0 14px 48px -12px var(--flou-glow-core)'
/**
 * The field's focus treatment.
 *
 * `authInputClass` carries `outline-none`, which suppresses the global
 * `:focus-visible` floor in `globals.css` — and that rule's own docblock allows
 * the suppression only for a component that draws its own indicator. This was
 * the whole of what it drew: one pixel of border at 60% alpha, on `:focus`
 * rather than `:focus-visible`. A keyboard user tabbing into the password box
 * got almost nothing (WCAG 2.4.7).
 *
 * The real indicator now lives beside it as `.flou-auth-field:focus-visible` in
 * `globals.css`, where it can be written unlayered and so beat the `outline-none`
 * utility. This border stays as the mouse-and-touch half of the same state.
 */
export const AUTH_INPUT_FOCUS = 'focus:border-[oklch(0.72_0.22_350)]/60'

const TOP_LINE = 'linear-gradient(90deg, transparent, oklch(0.78 0.22 350 / 0.40), transparent)'

const SURFACE =
  'radial-gradient(120% 100% at 50% 0%, color-mix(in oklab, var(--flou-ink) 6%, transparent) 0%, transparent 60%), color-mix(in oklab, var(--flou-surface) 82%, transparent)'

/**
 * The same card with the lights behind it left on — `/pl/login` and
 * `/pl/register` only.
 *
 * Owner: *„jakbyśmy ten panel zrobili półprzezroczysty, żeby całe logo było
 * widoczne"*. `AuthMarkBackdrop` puts a two-metre FLOU behind these two routes
 * and the card was sitting on the middle of it at 82% opacity under a 40px
 * backdrop blur, so the half of the mark that matters — the „LO" — was a grey
 * smudge. Both numbers had to move: dropping the fill alone leaves the blur
 * smearing the letterforms into a wash, and dropping the blur alone leaves them
 * behind an almost-opaque panel.
 *
 * 52% instead of 82%, and 7px of blur instead of 40. The blur is not zero on
 * purpose — it still separates the card from the shader's moving water, which
 * is what stops the glass reading as a hole. What it no longer does is destroy
 * the one thing the owner wants to see through it.
 *
 * The extra scrim is the price of the trade. At 52% the labels at `text-white/55`
 * were sitting on lit magenta, so the gradient's own dark stop is deepened and
 * a second, vertical wash is laid under the body of the card where the form is.
 * Contrast is not a style question here: these are the labels on a password
 * form.
 *
 * NOT the default, and that matters — `AuthCard` is also the checkout's
 * sign-in card, the checkout's contact card and the recruitment form, none of
 * which have a mark behind them to reveal. A see-through panel on an ordinary
 * page background is just a weak card.
 */
const SURFACE_SEE_THROUGH = [
  'linear-gradient(180deg, color-mix(in oklab, var(--flou-ink-inverse) 26%, transparent) 0%, color-mix(in oklab, var(--flou-ink-inverse) 19%, transparent) 46%, color-mix(in oklab, var(--flou-ink-inverse) 33%, transparent) 100%)',
  'radial-gradient(120% 100% at 50% 0%, color-mix(in oklab, var(--flou-ink) 8%, transparent) 0%, transparent 62%)',
  'color-mix(in oklab, var(--flou-surface) 52%, transparent)',
].join(', ')

export const authInputClass = cn(
  'w-full rounded-xl border border-white/12 bg-white/[0.06] px-4 py-3 text-sm text-white placeholder:text-white/55 outline-none transition-all duration-200 backdrop-blur-sm',
  AUTH_INPUT_FOCUS,
)

type Props = {
  children: React.ReactNode
  className?: string
  /** An anchor target, for a page that jumps to this card. */
  id?: string
  /** Renders as a `<form>` when supplied, so checkout can reuse the panel. */
  onSubmit?: (e: React.FormEvent) => void
  /** Let the backdrop through — see {@link SURFACE_SEE_THROUGH}. Auth routes only. */
  seeThrough?: boolean
}

export function AuthCard({ children, className, id, onSubmit, seeThrough }: Props) {
  const Tag = onSubmit ? 'form' : 'div'
  return (
    <Tag
      id={id}
      onSubmit={onSubmit}
      className={cn(
        'relative w-full overflow-hidden rounded-[var(--flou-r-lg)] border p-8',
        seeThrough
          ? 'border-white/[0.14] backdrop-blur-[7px] backdrop-saturate-[1.15]'
          : 'border-[var(--flou-line)] backdrop-blur-2xl',
        className,
      )}
      style={{
        background: seeThrough ? SURFACE_SEE_THROUGH : SURFACE,
        boxShadow: `0 48px 120px -36px rgba(0,0,0,0.95), ${AUTH_ACCENT_GLOW}`,
      }}
    >
      <div className="absolute inset-x-0 top-0 h-px" style={{ background: TOP_LINE }} />
      {children}
    </Tag>
  )
}

/** Small uppercase eyebrow used at the top of every auth card. */
export function AuthEyebrow({ children }: { children: React.ReactNode }) {
  return (
    <span className="block text-center text-[0.75rem] font-semibold tracking-[0.24em] text-white/55 uppercase">
      {children}
    </span>
  )
}

/** "albo" rule between social sign-in and the form. */
export function AuthDivider({ label = 'albo' }: { label?: string }) {
  return (
    <div className="my-5 flex items-center gap-3">
      <span className="h-px flex-1 bg-white/10" />
      <span className="text-[0.75rem] tracking-[0.16em] text-white/55 uppercase">{label}</span>
      <span className="h-px flex-1 bg-white/10" />
    </div>
  )
}

/**
 * Stacked label + input, matching the login form exactly.
 *
 * `ComponentPropsWithRef` rather than `InputHTMLAttributes`: the auth forms hold
 * a ref to every field so they can read what is actually in the box at submit
 * time instead of trusting React state — see `login-page.client.tsx` for why
 * that distinction is the difference between a working form and a silent one.
 */
export function AuthField({
  id,
  label,
  rightSlot,
  ...input
}: React.ComponentPropsWithRef<'input'> & {
  id: string
  label: string
  rightSlot?: React.ReactNode
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-[0.75rem] font-medium text-white/55">
        {label}
      </label>
      <div className="relative">
        <input
          id={id}
          {...input}
          /* `flou-auth-field`, not a utility: it is the hook for the two things
             Tailwind cannot express here — the `:-webkit-autofill` wash, which
             otherwise paints a pale opaque block inside the glass, and a real
             `:focus-visible` outline that has to out-rank `outline-none`. Both
             live in `globals.css`, unlayered, for exactly that reason. */
          className={cn(authInputClass, 'flou-auth-field', rightSlot && 'pr-20', input.className)}
        />
        {rightSlot && (
          <span className="absolute top-1/2 right-3 -translate-y-1/2">{rightSlot}</span>
        )}
      </div>
    </div>
  )
}
